import ICPemasukan2 from 'assets/icons/pemasukan-2.svg'
import ICPengeluaran2 from 'assets/icons/pengeluaran-2.svg'
import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import ExpandableNoteList from 'components/ExpandableNoteList'
import LihatDetail from 'components/Icons/LihatDetail'
import PMBPagination from 'components/PMBPagination'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import { TableComponent as DataTable } from 'components/TableComponent'
import { Can } from 'context/Acl'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  X,
} from 'react-feather'
import { PMBDateRangePicker } from 'components/PMBNewDatePicker'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import {
  SelectControlWithTestId,
} from 'components/PMBSelect/SelectInputWithTestId'
import {
  Badge,
  Card,
  CardBody,
  Col,
  Collapse,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledDropdown,
} from 'reactstrap'
import { formatDatePMB } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { ARUS_KAS_FORM, DETAIL_CASHFLOW } from 'utils/pathUrl'
import { BUSINESS_BOOKKEEPING, BUSINESS_BOOKKEEPING_COMPANY, MANAGE, READ } from 'utils/subjectActions'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import {
  useLazyGetArusKasQuery,
  useLazyGetArusKasPersonalQuery,
  useGetCategoriesCashFlowQuery,
  useGetCategoriesCashFlowPersonalQuery,
} from './cashFlowApiSlice'
import { setDate, setFetchOption, setFilters } from './cashFlowSlice'
import { formatNotes, handleDownloadExcel } from './downloadHistory'

const HistoryCashFlow = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { state } = useLocation()

  const hasPersonalAbility = useAbilityAccess({ action: READ, subject: BUSINESS_BOOKKEEPING })
  const hasCompanyAbility = useAbilityAccess({ action: READ, subject: BUSINESS_BOOKKEEPING_COMPANY })
  const showSwitcher = hasPersonalAbility && hasCompanyAbility

  // Calculate default dates (last 3 months)
  const defaultDateRange = useMemo(() => {
    const today = moment().endOf('day').toDate()
    const threeMonthsAgo = moment(today)
      .subtract(3, 'months')
      .startOf('day')
      .toDate()
    return [
      moment(threeMonthsAgo).format('YYYY-MM-DD'),
      moment(today).format('YYYY-MM-DD'),
    ]
  }, [])

  const [data, setData] = useState([])
  const { isOpenSidebarCashFlow, isOpenBanner, tag, filters } = useSelector(
    (s) => s.cashflow,
  )
  const { profile, ownerData } = useSelector((s) => s.dashboard)
  const { selectedDate, selectedFilter, selectedType, selectedCategory } = filters
  const [getArusKas, res] = useLazyGetArusKasQuery()
  const [getArusKasPersonal, resPersonal] = useLazyGetArusKasPersonalQuery()
  // Separate instances so the export fetch doesn't toggle the table's spinner
  const [getArusKasExport] = useLazyGetArusKasQuery()
  const [getArusKasPersonalExport] = useLazyGetArusKasPersonalQuery()

  // Determine effective type based on abilities
  // Default is personal, fallback to company if only company ability exists
  const effectiveType = !hasPersonalAbility && hasCompanyAbility
    ? 'company'
    : (selectedType || 'personal')
  const isPersonal = effectiveType === 'personal'
  const userId = isPersonal ? profile?.userId : ownerData?.userId

  const { data: dataCategoryCompany } = useGetCategoriesCashFlowQuery(
    { active: 1, userId: userId, allmanual: 1 },
    { skip: !userId || isPersonal }
  )

  const { data: dataCategoryPersonal } = useGetCategoriesCashFlowPersonalQuery(
    { active: 1, userId: userId, allmanual: 1 },
    { skip: !userId || !isPersonal }
  )

  const dataCategory = isPersonal ? dataCategoryPersonal : dataCategoryCompany
  const categoryOptions = Array.isArray(dataCategory) ? dataCategory.map(item => ({
    label: item.label,
    value: item.id,
    ...item
  })) : []

  const [selectedRow, setSelectedRow] = useState(null)
  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isShowFilter, setIsShowFilter] = useState(false)

  // HANDLE IS MOBILE
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleDateChange = (dates) => {
    dispatch(
      setFilters({
        selectedDate: dates,
      }),
    )
  }

  const handleFilterChange = (filter) => {
    dispatch(
      setFilters({
        selectedFilter: filter,
        selectedCategory: null,
      }),
    )
  }

  const handleCategoryChange = (category) => {
    dispatch(
      setFilters({
        selectedCategory: category,
      }),
    )
  }

  const handleFinishedDelete = async () => {
    if (!selectedDate || !selectedDate[0]) return

    const queryOptions = {
      startdate: selectedDate[0],
      enddate: selectedDate[1]
        ? moment(selectedDate[1]).local().add(1, 'days').format('YYYY-MM-DD')
        : selectedDate[0],
      filter: selectedFilter?.value,
      categoryId: selectedFilter?.value === 'manual' ? selectedCategory?.offlineID : undefined,
      page: tag?.page,
      size: tag?.size,
    }
    const resp = isPersonal
      ? await getArusKasPersonal(queryOptions)
      : await getArusKas(queryOptions)
    setData(resp?.data)
  }

  // Export every row matching the filters, not just the current page
  const handleExport = async () => {
    if (!selectedDate || !selectedDate[0]) return

    const queryOptions = {
      startdate: selectedDate[0],
      enddate: selectedDate[1]
        ? moment(selectedDate[1]).local().add(1, 'days').format('YYYY-MM-DD')
        : selectedDate[0],
      filter: selectedFilter?.value,
      categoryId: selectedFilter?.value === 'manual' ? selectedCategory?.offlineID : undefined,
      page: 0,
      size: data?.totalItems || tag?.size,
    }
    const fetchFn = isPersonal ? getArusKasPersonalExport : getArusKasExport
    try {
      const all = await fetchFn(queryOptions).unwrap()
      handleDownloadExcel({ data: all }, setIsDownload, setFirstRows, setTotalRows)
    } catch (e) {
      console.warn(e)
    }
  }

  const filterOptions = [
    { label: 'Semua catatan', value: 'all' },
    { label: 'Sistem Transaksi', value: 'transaksi' },
    { label: 'Sistem Inventori', value: 'inventory' },
    { label: 'Catat manual', value: 'manual' },
  ]

  const isDefaultDate = useMemo(() => {
    if (!selectedDate || !selectedDate[0]) return true
    return (
      moment(selectedDate[0]).format('YYYY-MM-DD') === defaultDateRange[0] &&
      moment(selectedDate[1]).format('YYYY-MM-DD') === defaultDateRange[1]
    )
  }, [selectedDate, defaultDateRange])

  // Calculate active filter count
  const activeFilterCount =
    (selectedFilter?.value !== 'all' ? 1 : 0) + (isDefaultDate ? 0 : 1) + (selectedFilter?.value === 'manual' && selectedCategory ? 1 : 0)

  // Reset all filters to default
  const handleResetFilter = () => {
    dispatch(
      setFilters({
        selectedFilter: filterOptions[0], // Reset to "Semua catatan"
        selectedDate: defaultDateRange,
        selectedCategory: null,
      }),
    )
  }

  const columns = [
    {
      name: <span>Tanggal</span>,
      cell: (row) =>
        formatDatePMB(
          `${row?.createddate + ' ' + row?.createdhour}`,
          `${row?.createddate + ' ' + row?.createdhour}`,
        ),
      minWidth: '150px',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: <span>Kategori</span>,
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center fw-500">
          <img
            width={24}
            height={24}
            src={
              row?.incomeorexpense === 'income' ? ICPemasukan2 : ICPengeluaran2
            }
            alt={
              row?.incomeorexpense === 'income' ? 'Pemasukan' : 'Pengeluaran'
            }
          />
          <span>{row?.label}</span>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: <span>Dari/Ke</span>,
      cell: (row) => <span>{row?.extradata?.picname ?? 'PasarMIKRO'}</span>,
      minWidth: '200px',
    },
    {
      name: <div>Jumlah</div>,
      cell: (row) => (
        <div className="d-flex justify-content-end w-100">
          <span>
            Rp
            {newFormatThousandSeparator(row?.amount, 0)}
          </span>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: <span>Jenis Catatan</span>,
      cell: (row) => {
        return (
          <div
            className={`badge rounded-pill ${row?.subType.toLowerCase() === 'sistem transaksi' ? 'bg-warning-50 text-warning-700' : row?.subType.toLowerCase() === 'catat manual' ? 'bg-gray-100 text-gray-700' : 'bg-danger-50 text-danger-700'}`}
          >
            {row?.subType}
          </div>
        )
      },
      minWidth: '150px',
    },
    {
      name: <span>Catatan</span>,
      wrap: true,
      cell: (row) => (
        <ExpandableNoteList
          notes={formatNotes(row?.notes)?.replace(/<br\s*\/?>/gi, ' ')}
        />
      ),
      minWidth: '200px',
    },
    {
      name: ' ',
      cell: (row) => (
        <LihatDetail
          onClick={() => {
            navigate(isPersonal ? `${DETAIL_CASHFLOW}?personal=true` : DETAIL_CASHFLOW, { state: { row } })
          }}
          id={row?.id}
        />
      ),
      center: true,
      minWidth: '60px',
      isFreeze: true,
      freezePosition: 'right',
    },
  ]

  useEffect(() => {
    if (!selectedDate || !selectedDate[0]) return

    dispatch(
      setDate({
        startdate: selectedDate[0],
        enddate: selectedDate[1]
          ? moment(selectedDate[1]).local().add(1, 'days').format('YYYY-MM-DD')
          : selectedDate[0],
      }),
    )

    const queryOptions = {
      startdate: selectedDate[0],
      enddate: selectedDate[1]
        ? moment(selectedDate[1]).local().add(1, 'days').format('YYYY-MM-DD')
        : selectedDate[0],
      filter: selectedFilter?.value,
      categoryId: selectedFilter?.value === 'manual' ? selectedCategory?.offlineID : undefined,
      page: tag?.page,
      size: tag?.size,
    }

    const fetchFn = isPersonal ? getArusKasPersonal : getArusKas

    fetchFn(queryOptions)
      .unwrap()
      .then((resAPI) => {
        setData(resAPI)
      })
      .catch((e) => {
        setData(null)
        if (
          e.response &&
          e.response.status === 403 &&
          typeof e.response.data.message !== 'undefined' &&
          e.response.data.message === 'access denied'
        ) {
          console.warn(e)
        }
        console.warn(e)
      })
  }, [selectedDate, selectedFilter, selectedCategory, effectiveType, tag])

  const handleTypeChange = (type) => {
    dispatch(
      setFilters({
        selectedType: type?.value,
      }),
    )
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Riwayat Arus Kas'} />
      </div>
      {/* SIDEBAR */}
      {/* <SidebarCashFlow
        handleFinishedDelete={handleFinishedDelete}
        row={selectedRow}
        isOpen={isOpenSidebarCashFlow}
        toggle={() => dispatch(toggleSidebarCashflow())}
        formatNotes={formatNotes}
      /> */}
      {/* PROGRESS DOWNLOAD */}
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />

      <div className="mb-2 d-flex flex-column gap-3">
        <Card className="border-0 shadow-sm">
          <CardBody>
            <Row className="align-items-center gy-2 mb-3">
              <Col xs={12} md="auto" className="order-md-1 d-flex flex-column flex-md-row align-items-center gap-3">
                <h5 className="fw-semibold mb-0">Riwayat Arus Kas</h5>
                {showSwitcher && (
                  <div className="d-flex bg-light rounded p-1 border">
                    <div
                      className={`px-3 py-1 rounded ${effectiveType !== 'personal' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-muted'}`}
                      style={{ cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px' }}
                      data-testid="cashflow-history-type-company"
                      onClick={() => handleTypeChange({ value: 'company' })}
                    >
                      Perusahaan
                    </div>
                    <div
                      className={`px-3 py-1 rounded ${effectiveType === 'personal' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-muted'}`}
                      style={{ cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px' }}
                      data-testid="cashflow-history-type-personal"
                      onClick={() => handleTypeChange({ value: 'personal' })}
                    >
                      Personal
                    </div>
                  </div>
                )}
              </Col>

              <Col xs={12} md="auto" className="order-md-2 ms-md-auto">
                <div className="d-flex gap-2 w-100">
                  {/* FILTER BUTTONS */}
                  {activeFilterCount > 0 && (
                    <div className="flex-shrink-0">
                      <CustomButton
                        onClick={handleResetFilter}
                        color="danger"
                        className="d-flex align-items-center justify-content-center"
                        testId="cashflow-history-reset-filter"
                        style={{
                          width: '40px',
                          height: '40px',
                          padding: 0,
                          minWidth: '40px',
                        }}
                      >
                        <X size={16} />
                      </CustomButton>
                    </div>
                  )}
                  <div className="flex-shrink-0">
                    <CustomButton
                      onClick={() => setIsShowFilter((prevState) => !prevState)}
                      color="primary"
                      outline
                      testId="cashflow-history-filter-toggle"
                      className="position-relative"
                    >
                      <div className="d-flex align-items-center gap-2 justify-content-center">
                        <Filter size={15} />
                        <span>Filter</span>
                        {activeFilterCount > 0 && (
                          <Badge
                            color="danger"
                            pill
                            style={{
                              position: 'absolute',
                              top: '-5px',
                              right: '-5px',
                              fontSize: '10px',
                            }}
                          >
                            {activeFilterCount}
                          </Badge>
                        )}
                        {isShowFilter ? (
                          <ChevronUp size={16} className="ml-2" />
                        ) : (
                          <ChevronDown size={16} className="ml-2" />
                        )}
                      </div>
                    </CustomButton>
                  </div>

                  <Can I={MANAGE} this={isPersonal ? BUSINESS_BOOKKEEPING : BUSINESS_BOOKKEEPING_COMPANY}>
                    <CustomButton
                      disabled={isDownload || !data?.items?.length}
                      className="rounded flex-shrink-0 text-center"
                      testId="cashflow-history-download"
                      onClick={handleExport}
                      color="primary"
                      outline
                    >
                      {isDownload ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <Download size={15} />
                      )}{' '}
                      Unduh
                    </CustomButton>

                    <UncontrolledDropdown className="flex-shrink-0">
                      <DropdownToggle
                        color="primary"
                        className="d-flex justify-content-center align-items-center gap-1"
                        data-testid="cashflow-history-add-button"
                      >
                        + Tambah
                      </DropdownToggle>
                      <DropdownMenu container="body">
                        <DropdownItem
                          onClick={() =>
                            navigate(ARUS_KAS_FORM, { state: { income: true, isPersonal } })
                          }
                          className="d-flex align-items-center gap-2"
                          data-testid="cashflow-history-add-income"
                        >
                          <ArrowDownCircle className="text-success" />
                          <span>Catat Pemasukan</span>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() =>
                            navigate(ARUS_KAS_FORM, {
                              state: { expense: true, isPersonal },
                            })
                          }
                          className="d-flex align-items-center gap-2"
                          data-testid="cashflow-history-add-expense"
                        >
                          <ArrowUpCircle className="text-danger" />
                          <span>Catat Pengeluaran</span>
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </Can>
                </div>
              </Col>
            </Row>

            {/* COLLAPSIBLE FILTER PANEL */}
            <Collapse isOpen={isShowFilter} className="mt-3">
              <div
                className="border rounded p-3 bg-light mb-3"
                style={{
                  width: '100%',
                  transition: 'all 0.3s ease-in-out',
                  opacity: isShowFilter ? 1 : 0,
                  transform: isShowFilter
                    ? 'translateY(0)'
                    : 'translateY(-10px)',
                }}
              >
                <Row className="g-2">
                  <Col xs={12} md={3} lg={3}>
                    <div
                      className="w-100"
                      style={{ width: '100%' }}
                    >
                      <PMBDateRangePicker
                        style={{ width: '100%', maxWidth: '100%' }}
                        testId="cashflow-history-date-range"
                        value={selectedDate}
                        onChange={handleDateChange}
                        format="yyyy-mm-dd"
                        onClear={() => {
                          dispatch(
                            setFilters({
                              selectedDate: defaultDateRange,
                            }),
                          )
                        }}
                        clearMode="callback"
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={3} lg={3}>
                    <div
                      className="w-100"
                      style={{ width: '100%' }}
                    >
                      <p className="mb-2">Jenis</p>
                      <Select
                        isSearchable={false}
                        onChange={handleFilterChange}
                        options={filterOptions}
                        value={selectedFilter}
                        classNamePrefix="select"
                        data-testid="cashflow-history-filter-select"
                        components={{ Control: SelectControlWithTestId }}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={3} lg={3}>
                    <div
                      className="w-100"
                      style={{ width: '100%' }}
                    >
                      <p className="mb-2">Kategori</p>
                      <Select
                        isClearable
                        isSearchable={false}
                        onChange={handleCategoryChange}
                        options={categoryOptions}
                        value={selectedCategory}
                        isDisabled={selectedFilter?.value !== 'manual'}
                        classNamePrefix="select"
                        data-testid="cashflow-history-category-select"
                        components={{ Control: SelectControlWithTestId }}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        placeholder="Pilih Kategori..."
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Collapse>

            <Card className="border-0 shadow-none">
              <DataTable
                columns={columns}
                data={data?.items}
                pagination
                paginationServer
                sortServer
                progressPending={isPersonal ? resPersonal?.isFetching : res?.isFetching}
                progressComponent={
                  <div style={{ margin: '100px 0px' }}>
                    <Spinner color="primary" />
                  </div>
                }
                sortIcon={<ChevronDown />}
                paginationComponent={(props) => (
                  <PMBPagination
                    rowCount={data?.totalItems || 0}
                    currentPage={tag?.page}
                    rowsPerPage={tag?.size}
                    testId="cashflow-history-pagination"
                    setCurrentPage={(e) => {
                      dispatch(setFetchOption({ page: e }))
                    }}
                    setRowsPerPage={(e) => {
                      dispatch(setFetchOption({ size: e }))
                    }}
                    props={props}
                  />
                )}
              />
            </Card>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default HistoryCashFlow
